<template>
	<HeroSection />

	<Row>
		<Container>
			<div class="mb-4 lg:mb-8 flex justify-between items-end">
				<h2
					id="scanner-pitch-title"
					class="text-white text-[32px] lg:text-6xl font-medium text-left"
				>
					News
				</h2>

				<LinkWithArrow
					text-color="!text-white hover:text-white/[.75]"
					label="All Breaking News"
					link="/news/"
					class="!hover:text-white/[.75]"
				/>
			</div>

			<MarketsHeroBanner
				id="featured"
				class="lg:col-span-2 lg:hidden mb-10"
				:news_list="news_list.slice(0, 4)"
				:alternative="true"
				:loaded="news_loaded"
			/>
			<div class="flex gap-8 mb-8 lg:mb-14 lg:gap-4">
				<MarketsHeroBanner
					id="featured"
					class="hidden lg:block"
					:news_list="news_list.slice(0, 4)"
					:alternative="true"
					:loaded="news_loaded"
				/>

				<TopGainerStocks
					id="top-gainers-header"
					class="lg:max-w-[340px] w-full"
					:alternative="true"
				/>
			</div>
			<MarketsCoverageRow
				id="coverage"
				:news="news_list.slice(4, 10)"
				:alternative="true"
				:loaded="news_loaded"
			/>
		</Container>
	</Row>

	<ScannersPitchGridSection />

	<FeaturesSection />

	<TestimonialSlider />

	<TrainersSection />

	<Slider />

	<ByTheNumbers />

	<ClientOnly>
		<Row>
			<Container>
				<PopularPostsSection />
			</Container>
		</Row>
	</ClientOnly>
	<SttOnYoutubeDesktop />
	<Head v-if="page && page.yoast_meta">
		<Title>StocksToTrade | The All-In-One Trading Platform</Title>

		<Link
			:href="current_hostname.slice(0, -1)"
			rel="canonical"
		/>
		<Meta
			:content="current_hostname.slice(0, -1)"
			property="og:url"
		/>
		<Meta
			:content="`${current_hostname}images/stt-og-image.webp`"
			property="og:image"
		/>

		<template
			v-for="(tag, i) in page.yoast_meta"
			:key="i"
		>
			<Meta
				:property="tag.property"
				:content="tag.content"
				v-if="
					tag.property && tag.property !== 'og:url' && tag.property !== 'og:image'
				"
			/>
			<Meta
				:name="tag.name"
				:content="tag.content"
				v-else-if="
					tag.name &&
					![
						'canonical',
						'robots',
						'WordPressAuthor:YoastHeadJson_Schema_',
						'schema:json',
						'title'
					].includes(tag.name)
				"
			/>
		</template>
	</Head>
</template>

<script setup>
import anime from "animejs";
import axios from "axios";
import { useStocksStore } from "@/stores/useStocksStore";

const { current_hostname, songbirdProxy } = useRuntimeConfig().public;
const stocksStore = useStocksStore();
const news_list = ref([]);
const news_loaded = ref(false);

const schema = {
	"@context": "https://schema.org",
	"@graph": [
		{
			"@type": "WebPage",
			"@id": `${current_hostname}/`,
			url: `${current_hostname}/`,
			name: "StocksToTrade.com: Homepage",
			isPartOf: { "@id": `${current_hostname}/#website` },
			datePublished: "2013-11-25T21:44:59+00:00",
			dateModified: "2019-08-26T18:57:26+00:00",
			description:
				"Thousands of Beginner and Advanced Traders. Abandon Their Old Trading Tools.",
			inLanguage: "en-US"
		},
		{
			"@type": "WebSite",
			"@id": `${current_hostname}/#website`,
			url: `${current_hostname}/`,
			name: "StocksToTrade",
			description:
				"A powerful enough platform for hedgefunds, built for the average-joe trader",
			publisher: { "@id": `${current_hostname}/#organization` },
			inLanguage: "en-US"
		},
		{
			"@type": "Organization",
			"@id": `${current_hostname}/#organization`,
			name: "StocksToTrade",
			url: `${current_hostname}/`,
			logo: {
				"@type": "ImageObject",
				"@id": `${current_hostname}/#/schema/logo/image/`,
				url: `${current_hostname}/wp-content/uploads/2017/11/stticon.png`,
				contentUrl: `${current_hostname}/wp-content/uploads/2017/11/stticon.png`,
				width: 1000,
				height: 1000,
				caption: "StocksToTrade"
			},
			image: { "@id": `${current_hostname}/#/schema/logo/image/` },
			sameAs: [
				"https://www.facebook.com/people/Stockstotrade/100087191765235/",
				"https://x.com/stockstotrade",
				"https://www.instagram.com/stockstotrade/",
				"https://www.youtube.com/channel/UC54LMqQyA-IA0sNZ2qjH62w"
			]
		}
	]
};

const { data } = await useAsyncData("home", async ({ $config }) => {
	try {
		const { data } = await axios.get(
			$config.public.hostname + "wp-json/wp/v2/pages/4"
		);
		if (data) {
			return { page: data };
		}
	} catch (e) {
		console.log(e);
	}
});

const page = data.value?.page ?? null;

// Core Data - matching PHP configuration
const funnel_id = "stt_blog"; // Funnel ID for this page
const page_id = "stt-homepage-optin";
const iterable_list_id = "6062859"; // Iterable list used for the optin of this funnel

// Tracking APIs and Script initialization parameters - matching PHP configuration
const tracking_key =
	"azVFNGlxKXcxZEhuekdremIzZnpyUV5pQG9AeiFaaVk9by1ILkFUdjolSDpSSX1fLDhwM2o6IVBzPnF1dVNqVw==";
const tracking_host = "tnd389f0za";
const mkt_key = "3W)UW4FU{sk2TX@D,r-3ss^+qeQ'xL]q";
const mkt_host = "mktlap";
const mkt_tld = "stockstotrade.com";

const debug = true; // Set to False for production environment - true for debugging and dev

const mkt_script_url = "https://hgevt001.stockstotrade.com/mkt-weet.js";

// Generate a random number between -10 and +15
const randomNumber = (position = 0) => {
	const value = Math.floor(Math.random() * (15 + -(-15) + 1)) + -15;

	let pos = 0;
	if (value > 0) {
		pos = position + value;
	} else {
		pos = position - value;
	}

	return pos;
};

const animationObserver = async () => {
	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry, i) => {
			if (entry.isIntersecting) {
				const element = entry.target;
				const animateFlag = element.getAttribute("data-animate");
				if (animateFlag === "true") {
					if (entry.target.id === "youtube-thumbnails") {
						anime({
							targets: "#youtube-thumbnails .youtube-item",
							opacity: [0, 1],

							delay: anime.stagger(250),
							easing: "easeInOutQuad"
						});
					}

					if (entry.target.id === "stay-ahead-main-image") {
						anime({
							targets: "#stay-ahead-main-image",
							opacity: [0, 1],
							easing: "easeInOutQuad",
							translateY: [350, 0],
							duration: 600,
							delay: 50
						});
					}

					if (entry.target.classList.contains("data-circle")) {
						let initialPos = entry.target.getAttribute("data-position");

						initialPos = initialPos ? Number(initialPos) : 0;

						const dataCircleIndex = entry.target.getAttribute("data-stat");

						anime({
							targets: `#${entry.target.id}`,
							opacity: [0, 1],
							easing: "easeInOutQuad",
							duration: 250,
							delay: dataCircleIndex ? dataCircleIndex * 50 : 0
						});

						anime({
							targets: `#${entry.target.id}`,
							easing: "cubicBezier(.5, .05, .1, .3)",
							direction: "alternate",
							translateY: [
								{ value: randomNumber(initialPos), duration: 1000 },
								{ value: randomNumber(initialPos), duration: 1000 },
								{ value: randomNumber(initialPos), duration: 1000 },
								{ value: randomNumber(initialPos), duration: 1000 }
							],
							translateX: [
								{ value: randomNumber(), duration: 1000 },
								{ value: randomNumber(), duration: 1000 },
								{ value: randomNumber(), duration: 1000 },
								{ value: randomNumber(), duration: 1000 }
							],
							loop: true,
							delay: dataCircleIndex ? dataCircleIndex * 250 : 0
						});
					}

					element.setAttribute("data-animate", "false");

					observer.unobserve(element);
				}
			}
		});
	});

	const animatedElements = await document.querySelectorAll("[data-animate]");
	await animatedElements.forEach((element) => {
		observer.observe(element);
	});
};
onMounted(async () => {
	const wistia_script = document.createElement("script");
	wistia_script.src = "https://fast.wistia.com/assets/external/E-v1.js";
	wistia_script.async = true;

	if (import.meta.client) {
		// Start all promises concurrently
		const newsPromise = axios
			.get(`${songbirdProxy}stocks/news`, {
				params: {
					list: true
				}
			})
			.catch((err) => {
				console.error("[HOME] Error fetching news:", err);
				return { data: { items: [] } };
			});

		const [news_list_response] = await Promise.all([newsPromise]);

		const items = news_list_response?.data?.items;
		if (Array.isArray(items) && items.length > 0) {
			news_list.value = items.map((item) => ({
				...item,
				slug: `/news/${item.slug}/`,
				link: `/news/${item.slug}/`
			}));

			items.forEach((item) => {
				if (item?.stock_pairs?.length > 0) {
					stocksStore.addSubscribedSymbolsFromPairs(item.stock_pairs);
				}
			});
		}
		news_loaded.value = true;
		// Intersection Observer setup
		await animationObserver();
	}
});

// Using the exact page ID from the working configuration
useHead({
	script: [
		...(schema
			? [
					{
						type: "application/ld+json",
						class: "saswp-schema-markup-output",
						innerHTML: schema
					}
				]
			: []),
		{
			type: "application/ld+json",
			class: "saswp-schema-markup-output",
			innerHTML: schema
		},
		{
			innerHTML: `
				window.weet_page_id = '${page_id}';
				window.weet_page_type = 'optin';
				window.weet_page_rev = 'A';
				window.weet_funnel_id = '${funnel_id}';

				// Console log the window object values
				console.log('Window WEET values:', {
					weet_page_id: window.weet_page_id,
					weet_page_type: window.weet_page_type,
					weet_page_rev: window.weet_page_rev,
					weet_funnel_id: window.weet_funnel_id
				});
			`
		},
		{
			async: true,
			id: "mktlsid",
			type: "text/javascript",
			src: mkt_script_url,
			"data-h": tracking_host,
			"data-k": tracking_key,
			"data-l": debug ? "1" : undefined, // DEBUG server side logging - set to 1 to log server side
			"data-dbg": debug ? "0" : undefined, // DEBUG debug without tracking - set to 1 to log without tracking event
			"data-dbglog": debug ? "1" : undefined, // DEBUG debug with tracking - set to 1 to log with tracking event
			"data-ep": debug ? "test" : undefined, // DEBUG event prefix
			"data-mak": mkt_key,
			"data-mtld": mkt_tld,
			"data-mhst": mkt_host,
			"data-phf": "1",
			"data-mgp": "1",
			"data-mofc": "optin_form",
			"data-mlo": iterable_list_id,
			body: true
		}
	]
});
</script>

<style lang="scss" scoped>
@use "~/assets/utils.scss" as *;

h1 {
	@include breakpoint(lg) {
		font-size: 90px;
	}
}

[data-animate="true"] {
	opacity: 0;
}
</style>
